import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import VideoCall from '../components/VideoCall';
import type { Call } from '../types/call';

const CallScreen: React.FC = () => {
  const { callId } = useParams<{ callId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [call, setCall] = useState<Call | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCall = async () => {
      if (!callId || !user) return;

      try {
        const callDoc = await getDoc(doc(db, 'calls', callId));
        if (!callDoc.exists()) {
          setError('Call not found');
          return;
        }

        const callData = callDoc.data() as Call;
        
        // Verify user is allowed in this call
        if (callData.starId !== user.uid && callData.fanId !== user.uid) {
          setError('You are not authorized to join this call');
          return;
        }

        // Check if call time is valid
        const now = new Date();
        const startTime = new Date(callData.startTime);
        const endTime = new Date(callData.endTime);

        if (now < startTime) {
          setError('This call has not started yet');
          return;
        }

        if (now > endTime) {
          setError('This call has ended');
          return;
        }

        setCall(callData);
      } catch (err) {
        console.error('Error fetching call:', err);
        setError('Failed to load call details');
      }
    };

    fetchCall();
  }, [callId, user]);

  const handleCallEnded = () => {
    navigate('/dashboard'); // Or wherever you want to redirect after call
  };

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-900">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  if (!call || !user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-slate-900">
      <VideoCall 
        call={call} 
        userId={user.uid} 
        onCallEnded={handleCallEnded} 
      />
    </div>
  );
};

export default CallScreen; 